import { sdgAlignmentLevelToValence } from '@upright/common/model/output/scores/sdg-alignment/shared';
import {
  alignmentLevelsByValence,
  allSdgAlignmentLevels,
  allSdgIds,
  ByAlignmentLevel,
  BySdg,
  MultiplierByAlignmentLevel,
  SdgAlignment,
  SdgAlignmentByLevel,
  SdgAlignmentLevel,
  SdgAlignmentsBySdgAndLevel,
  SdgAlignmentStrength,
  SdgAlignmentValence,
  SdgId,
  SdgUnitsMode,
} from '@upright/common/model/output/scores/sdg-alignment/types';
import { safeVersionGte } from '@upright/common/utils';
import { chain, compact, flatMap, get, sum, uniq } from 'lodash';
import { revertAlignmentLevelMultiplier } from './alignment-levels';
import { SDG_PURE_REVENUE_SHARES_MIN_RELEASE } from './constants';

export function getMostAlignedSdgs(
  alignments: SdgAlignmentsBySdgAndLevel | undefined,
  valence: SdgAlignmentValence,
  includeAll: boolean,
) {
  if (!alignments) {
    return [];
  }

  // OA: Gotta copy the array here or the original allSdgIds will get sorted, resulting in some surprises (-_-;)
  const sortedSdgIds = [...allSdgIds].sort((a, b) => {
    const sortScore =
      (getSummedAlignment(alignments[b], valence) || 0) - (getSummedAlignment(alignments[a], valence) || 0);
    return sortScore;
  });

  const topSdgIds = sortedSdgIds.slice(0, 3);
  const filteredSdgIds = topSdgIds.filter(sdgId => {
    const alignment = getSummedAlignment(alignments[sdgId], valence);
    if (includeAll) {
      return alignment > 0;
    }
    return alignment > 0.01;
  });
  return filteredSdgIds;
}

export function getSummedAlignment(
  alignments: SdgAlignmentByLevel | undefined,
  valence: SdgAlignmentValence,
  unitsMode: SdgUnitsMode = SdgUnitsMode.SUMMARY_PERCENTAGE,
) {
  if (!alignments) {
    return 0;
  }
  const { strong, normal, weak } = getAlignmentsOfValenceWithAdjustedValues(alignments, valence, unitsMode);
  return strong.adjustedValue + normal.adjustedValue + weak.adjustedValue;
}

export function getMaxAlignment(alignments: SdgAlignmentsBySdgAndLevel | undefined) {
  if (!alignments) {
    return 0;
  }
  return allSdgIds.reduce((maxSoFar, sdgId) => {
    const alignmentsOfSdg = alignments[sdgId];
    const aligned = getSummedAlignment(alignmentsOfSdg, SdgAlignmentValence.ALIGNED);
    const misaligned = getSummedAlignment(alignmentsOfSdg, SdgAlignmentValence.MISALIGNED);
    return Math.max(maxSoFar, aligned, misaligned);
  }, 0);
}

export const companyAlignmentLevelMultipliers: MultiplierByAlignmentLevel = {
  [SdgAlignmentLevel.STRONGLY_ALIGNED]: 1,
  [SdgAlignmentLevel.STRONGLY_MISALIGNED]: 1,
  [SdgAlignmentLevel.ALIGNED]: 0.5,
  [SdgAlignmentLevel.MISALIGNED]: 0.5,
  [SdgAlignmentLevel.WEAKLY_ALIGNED]: 0.25,
  [SdgAlignmentLevel.WEAKLY_MISALIGNED]: 25,
};

function getSdgMagicMultiplier(level: SdgAlignmentLevel, _sdg: SdgId) {
  const multiplier = companyAlignmentLevelMultipliers[level];
  if (!multiplier) {
    throw Error('Alignment level multiplier missing');
  }
  return multiplier;
}

export function getLegacyTotalAlignment(
  alignmentsByLevel: SdgAlignmentByLevel,
  valence: SdgAlignmentValence,
  sdgId: SdgId,
) {
  const levels = Object.keys(alignmentsByLevel) as SdgAlignmentLevel[];
  const filteredLevels = levels.filter(l => sdgAlignmentLevelToValence(l) === valence);
  const totalAlignment: number | undefined = sum(
    filteredLevels.map(level => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const alignment = alignmentsByLevel[level]!;
      const multiplier = getSdgMagicMultiplier(level, sdgId);
      return alignment.alignment * multiplier;
    }),
  );
  const targets = uniq(
    compact(
      flatMap(
        filteredLevels.map(level => {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const alignment = alignmentsByLevel[level]!;
          return alignment.targets;
        }),
      ),
    ),
  );
  return { totalAlignment, targets };
}

/**
 * Given a magic-level-multiplier normalized alignment and alignment level, get pure revenue share that is aligned with the given SDG on the given level.
 *
 * Background: Moderate weak SDG alignments are scaled down to represent the strong alignment intensity in company variant computation.
 * TODO: migrate old results to remove the need for rescaling
 *
 * @param alignments Company or weight set SDG alginments
 *
 */
export function getSdgAlignedRevenueShare(
  alignments: Partial<BySdg<ByAlignmentLevel<SdgAlignment>>>,
  sdgId: SdgId,
  level: SdgAlignmentLevel,
) {
  return revertAlignmentLevelMultiplier(alignments[sdgId]?.[level]?.alignment || 0, level);
}

export function friendlySdgValence(valence: SdgAlignmentValence) {
  switch (valence) {
    case SdgAlignmentValence.ALIGNED:
      return 'Aligned';
    case SdgAlignmentValence.MISALIGNED:
      return 'Misaligned';
  }
}

export function getAlignmentsOfValenceWithAdjustedValues(
  alignments: ByAlignmentLevel<SdgAlignment> | undefined,
  valence: SdgAlignmentValence,
  unitsMode: SdgUnitsMode,
) {
  const alignmentsWithAdjustedValues = chain(alignmentLevelsByValence[valence])
    .map(alignmentLevel => {
      const alignmentOfLevel = get(alignments, alignmentLevel, undefined);
      return {
        alignment: alignmentOfLevel,
        valence,
        strength: toSdgAlignmentStrength(alignmentLevel),
        adjustedValue: toAlignmentValueForUnitMode(alignmentOfLevel?.alignment, alignmentLevel, unitsMode),
      };
    })
    .keyBy(d => d.strength)
    .value();
  return {
    strong: alignmentsWithAdjustedValues[SdgAlignmentStrength.STRONG],
    normal: alignmentsWithAdjustedValues[SdgAlignmentStrength.NORMAL],
    weak: alignmentsWithAdjustedValues[SdgAlignmentStrength.WEAK],
  };
}

function toAlignmentValueForUnitMode(
  magicAlignmentValue: number | undefined,
  level: SdgAlignmentLevel,
  unitMode: SdgUnitsMode,
) {
  if (magicAlignmentValue === undefined) {
    return 0; // TODO: should probably return undefined
  }
  if (unitMode === SdgUnitsMode.SUMMARY_PERCENTAGE) return magicAlignmentValue;
  return revertAlignmentLevelMultiplier(magicAlignmentValue, level);
}

function toSdgAlignmentStrength(alignmentLevel: SdgAlignmentLevel): SdgAlignmentStrength {
  switch (alignmentLevel) {
    case SdgAlignmentLevel.STRONGLY_ALIGNED:
    case SdgAlignmentLevel.STRONGLY_MISALIGNED:
      return SdgAlignmentStrength.STRONG;
    case SdgAlignmentLevel.ALIGNED:
    case SdgAlignmentLevel.MISALIGNED:
      return SdgAlignmentStrength.NORMAL;
    case SdgAlignmentLevel.WEAKLY_ALIGNED:
    case SdgAlignmentLevel.WEAKLY_MISALIGNED:
      return SdgAlignmentStrength.WEAK;
  }
}

export function toSdgAlignmentLevel(valence: SdgAlignmentValence, strength: SdgAlignmentStrength) {
  switch (strength) {
    case SdgAlignmentStrength.WEAK: {
      return valence === SdgAlignmentValence.ALIGNED
        ? SdgAlignmentLevel.WEAKLY_ALIGNED
        : SdgAlignmentLevel.WEAKLY_MISALIGNED;
    }
    case SdgAlignmentStrength.NORMAL: {
      return valence === SdgAlignmentValence.ALIGNED ? SdgAlignmentLevel.ALIGNED : SdgAlignmentLevel.MISALIGNED;
    }
    case SdgAlignmentStrength.STRONG: {
      return valence === SdgAlignmentValence.ALIGNED
        ? SdgAlignmentLevel.STRONGLY_ALIGNED
        : SdgAlignmentLevel.STRONGLY_MISALIGNED;
    }
  }
}

export function isSdgId(candidate: string): candidate is SdgId {
  return allSdgIds.includes(candidate as SdgId);
}

export function assertIsSdgId(candidate: string): asserts candidate is SdgId {
  if (!isSdgId(candidate)) throw Error(`Invalid SdgId ${candidate}`);
}

export function isSdgAlignmentLevel(candidate: string): candidate is SdgAlignmentLevel {
  return allSdgAlignmentLevels.includes(candidate as SdgAlignmentLevel);
}

export function assertIsSdgAlignmentLevel(candidate: string): asserts candidate is SdgAlignmentLevel {
  if (!isSdgAlignmentLevel(candidate)) throw Error(`Invalid SdgAlignmentLevel ${candidate}`);
}

export function doesReleaseSupportPureRevenueShares(release: string) {
  return safeVersionGte(release, SDG_PURE_REVENUE_SHARES_MIN_RELEASE);
}
